import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App';
import { authAxios } from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function DraftInterface() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [draftState, setDraftState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [makingPick, setMakingPick] = useState(false);
  const [selectedContestant, setSelectedContestant] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    if (currentSeason && currentSeason.draft_status === 'in_progress') {
      loadDraftState();
    }
  }, [currentSeason]);

  useEffect(() => {
    // Auto-refresh every 3 seconds when draft is active
    if (autoRefresh && currentSeason?.draft_status === 'in_progress') {
      const interval = setInterval(() => {
        loadDraftState();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, currentSeason]);

  // Pick clock countdown (server sends the deadline; a background job enforces it)
  useEffect(() => {
    const deadline = draftState?.current_pick_deadline;
    if (!deadline) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.round((new Date(deadline) - new Date()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [draftState?.current_pick_deadline]);

  const loadDraftState = async () => {
    try {
      const response = await authAxios.get(`${API}/seasons/${currentSeason.id}/draft/state`);
      setDraftState(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load draft state:', error);
      // Stop polling if we get a 401 (session expired)
      if (error.response?.status === 401) {
        setAutoRefresh(false);
      }
      setLoading(false);
    }
  };

  const makePick = async (contestantId) => {
    try {
      setMakingPick(true);
      await authAxios.post(
        `${API}/seasons/${currentSeason.id}/draft/pick`,
        null,
        { params: { contestant_id: contestantId } }
      );
      toast.success('Pick made!');
      setSelectedContestant(null);
      loadDraftState();
    } catch (error) {
      console.error('Failed to make pick:', error);
      toast.error(error.response?.data?.detail || 'Failed to make pick');
    } finally {
      setMakingPick(false);
    }
  };

  if (!currentSeason || currentSeason.draft_status !== 'in_progress') {
    return null;
  }

  if (loading || !draftState) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading draft...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentPlayer = draftState.active_players.find(p => p.id === draftState.current_player_id);
  const isUserTurn = draftState.is_user_turn;

  return (
    <div className="space-y-6 mb-6">
      {/* Draft Status Banner */}
      <Card className={isUserTurn ? 'border-2 border-amber-500' : ''}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-1">
                {draftState.is_complete ? '🎉 Draft Complete!' : '📋 Draft In Progress'}
              </h2>
              {!draftState.is_complete && (
                <p className="text-lg text-slate-600">
                  Round {draftState.current_round} of {draftState.total_rounds} • Pick #{draftState.current_pick_number}
                </p>
              )}
            </div>
            {!draftState.is_complete && (
              <div className="text-right">
                <p className="text-sm text-slate-600 mb-1">Current Pick:</p>
                <p className={`text-2xl font-bold ${isUserTurn ? 'text-amber-600' : 'text-slate-800'}`}>
                  {isUserTurn ? 'YOUR TURN!' : currentPlayer?.name || 'Unknown'}
                </p>
                {secondsLeft !== null && (
                  <div data-testid="draft-pick-clock" className="mt-1">
                    <p className={`text-lg font-bold tabular-nums ${secondsLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>
                      {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')} on the clock
                    </p>
                    <p className="text-xs text-slate-500">
                      {secondsLeft === 0
                        ? 'Time expired — auto-drafting a random castaway...'
                        : 'If time runs out, a random available castaway is auto-drafted'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Your Picks Summary */}
      {draftState.user_picks.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Your Picks ({draftState.user_picks.length})
            </h3>
            <div className="flex flex-wrap gap-3">
              {draftState.user_picks.map(pick => {
                const contestant = draftState.all_picks.find(p => p.pick_number === pick.pick_number);
                return (
                  <div key={pick.pick_number} className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                    <span className="text-xs font-semibold text-slate-600">R{pick.round_number}</span>
                    <span className="text-sm font-medium">{contestant?.contestant_name}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Contestants */}
      {!draftState.is_complete && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                Available Contestants ({draftState.available_contestants.length})
              </h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                Auto-refresh
              </label>
            </div>
            
            {isUserTurn ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {draftState.available_contestants.map(contestant => {
                  // In double draft, check if this contestant was already picked by others
                  const pickedBy = draftState.draft?.double_draft_enabled 
                    ? draftState.all_picks.filter(p => p.contestant_id === contestant.id)
                    : [];
                  const isAlreadyPicked = pickedBy.length > 0;
                  
                  return (
                    <div
                      key={contestant.id}
                      className={`cursor-pointer rounded-lg border-2 transition-all hover:shadow-lg relative ${
                        selectedContestant === contestant.id
                          ? 'border-amber-500 bg-amber-50'
                          : isAlreadyPicked
                          ? 'border-blue-300 bg-blue-50/50 hover:border-amber-300'
                          : 'border-slate-200 hover:border-amber-300'
                      }`}
                      onClick={() => setSelectedContestant(contestant.id)}
                    >
                      {/* Badge showing how many have picked this contestant */}
                      {isAlreadyPicked && (
                        <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md z-10">
                          {pickedBy.length}
                        </div>
                      )}
                      <img
                        src={contestant.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(contestant.full_name)}&background=64748b&color=fff&size=150`}
                        alt={contestant.full_name}
                        className="w-full h-32 object-cover rounded-t-lg"
                        onError={(e) => {
                          const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(contestant.full_name)}&background=64748b&color=fff&size=150`;
                          if (e.target.src !== fallback) {
                            e.target.onerror = null;
                            e.target.src = fallback;
                          }
                        }}
                        referrerPolicy="no-referrer"
                      />
                      <div className="p-2 text-center">
                        <p className="font-semibold text-sm">{contestant.full_name}</p>
                        {contestant.age && (
                          <p className="text-xs text-slate-600">{contestant.age} yrs</p>
                        )}
                        {/* Show who already picked this contestant */}
                        {isAlreadyPicked && (
                          <div className="text-xs text-blue-600 mt-1">
                            <span>Picked by: </span>
                            {pickedBy.map((p, idx) => (
                              <span key={p.player_user_id} className="inline-flex items-center gap-1">
                                {idx > 0 && ', '}
                                <span 
                                  className="w-2 h-2 rounded-full inline-block" 
                                  style={{ backgroundColor: p.player_color || '#3b82f6' }}
                                />
                                {p.player_name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-600 mb-2">Waiting for {currentPlayer?.name} to pick...</p>
                <div className="animate-pulse text-4xl">⏳</div>
              </div>
            )}

            {isUserTurn && selectedContestant && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-semibold text-amber-900">
                    Ready to pick {draftState.available_contestants.find(c => c.id === selectedContestant)?.full_name}?
                  </p>
                  <p className="text-sm text-amber-700">This will be your Round {draftState.current_round} pick</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSelectedContestant(null)}>
                    Cancel
                  </Button>
                  <Button onClick={() => makePick(selectedContestant)} disabled={makingPick}>
                    {makingPick ? 'Confirming...' : 'Confirm Pick'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Draft Board */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Draft Board</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Pick</th>
                  <th className="px-3 py-2 text-left">Round</th>
                  <th className="px-3 py-2 text-left">Player</th>
                  <th className="px-3 py-2 text-left">Contestant</th>
                </tr>
              </thead>
              <tbody>
                {draftState.all_picks.map(pick => (
                  <tr key={pick.pick_number} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold">#{pick.pick_number}</td>
                    <td className="px-3 py-2">{pick.round_number}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: pick.player_color || '#3b82f6' }}
                        />
                        {pick.player_name}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {pick.contestant_photo && (
                          <img
                            src={pick.contestant_photo}
                            alt={pick.contestant_name}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <span className="font-medium">{pick.contestant_name}</span>
                        {pick.auto_assigned && (
                          <span
                            className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-semibold uppercase tracking-wide"
                            title="Auto-drafted because the pick clock expired"
                            data-testid={`auto-pick-badge-${pick.pick_number}`}
                          >
                            Auto
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!draftState.is_complete && (
                  <tr className="border-t bg-amber-50">
                    <td className="px-3 py-2 font-semibold">#{draftState.current_pick_number}</td>
                    <td className="px-3 py-2">{draftState.current_round}</td>
                    <td className="px-3 py-2 font-semibold text-amber-700">
                      {currentPlayer?.name} {isUserTurn && '(YOU)'}
                    </td>
                    <td className="px-3 py-2 text-slate-500 italic">
                      {isUserTurn ? 'Make your pick above...' : 'Picking...'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
